import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { resolveLocation, stateMatchesPolygon } from "./Stage3GeoData";

const Stage3GeographyMapCity = ({ selectedCity }) => {
  const containerRef = useRef(null);
  const instanceRef = useRef(null);

  useEffect(() => {
    const initMap = () => {
      // Wait for amCharts scripts
      if (
        !window.am5 ||
        !window.am5map ||
        !window.am5geodata_indiaLow ||
        !window.am5themes_Animated
      ) {
        setTimeout(initMap, 200);
        return;
      }

      // Prevent multiple roots
      if (instanceRef.current) return;

      const root = window.am5.Root.new(containerRef.current);
      root.setThemes([window.am5themes_Animated.new(root)]);

      const chart = root.container.children.push(
        window.am5map.MapChart.new(root, {
          panX: "translateX",
          panY: "translateY",
          projection: window.am5map.geoMercator(),
        })
      );

      const indiaSeries = chart.series.push(
        window.am5map.MapPolygonSeries.new(root, {
          geoJSON: window.am5geodata_indiaLow,
        })
      );

      indiaSeries.mapPolygons.template.setAll({
        fill: window.am5.color(0xd3d3d3),
        stroke: window.am5.color(0xffffff),
        strokeWidth: 0.8,
        tooltipText: "{name}",
      });

      // Create point (marker) series
      const pointSeries = chart.series.push(
        window.am5map.MapPointSeries.new(root, {})
      );

      pointSeries.bullets.push(() => {
        return window.am5.Bullet.new(root, {
          sprite: window.am5.Circle.new(root, {
            radius: 4,
            fill: window.am5.color(0xff0000),
            stroke: window.am5.color(0xffffff),
            strokeWidth: 1.5,
          }),
        });
      });

      chart.appear(1000, 100);

      instanceRef.current = { root, chart, indiaSeries, pointSeries };
    };

    initMap();

    return () => {
      if (instanceRef.current?.root) {
        instanceRef.current.root.dispose();
        instanceRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const clear = () => {
      if (!instanceRef.current) return;
      const { indiaSeries, pointSeries } = instanceRef.current;
      indiaSeries.mapPolygons.each((p) =>
        p.set("fill", window.am5.color(0xd3d3d3))
      );
      pointSeries.data.setAll([]);
    };

    if (!selectedCity) {
      clear();
      return;
    }

    const apply = () => {
      if (cancelled) return;
      // map may still be booting — retry until the amCharts instance exists
      if (!instanceRef.current) {
        setTimeout(apply, 150);
        return;
      }

      const { indiaSeries, chart, pointSeries } = instanceRef.current;
      const loc = resolveLocation(selectedCity);

      indiaSeries.mapPolygons.each((p) =>
        p.set("fill", window.am5.color(0xd3d3d3))
      );

      if (!loc.matched) {
        pointSeries.data.setAll([]);
        return;
      }

      // Highlight the state(s) the selection belongs to
      indiaSeries.mapPolygons.each((p) => {
        const nm = p.dataItem?.dataContext?.name;
        if (loc.states.some((s) => stateMatchesPolygon(nm, s))) {
          p.set("fill", window.am5.color(0xff5733));
        }
      });

      // Drop a marker + zoom to the resolved point
      if (loc.point) {
        pointSeries.data.setAll([
          {
            geometry: {
              type: "Point",
              coordinates: [loc.point.lon, loc.point.lat],
            },
          },
        ]);

        const zoom = loc.kind === "city" ? 5 : loc.kind === "state" ? 4 : 3;
        chart.zoomToGeoPoint(
          { latitude: loc.point.lat, longitude: loc.point.lon },
          zoom,
          true
        );
      } else {
        pointSeries.data.setAll([]);
      }
    };

    apply();
    return () => {
      cancelled = true;
    };
  }, [selectedCity]);

  return <MapWrapper ref={containerRef} />;
};

export default Stage3GeographyMapCity;

const MapWrapper = styled.div`
  width: 100%;
  height: 180px;
  border-radius: 8px;
  background: #f8fafc;
  overflow: hidden;
`;
